import { fitLogo, rasterizeLogo, type ReceiptLogo } from './receiptLogoRaster';

export type { ReceiptLogo } from './receiptLogoRaster';

const LOG = '[receipt-logo]';

const memory = new Map<string, ReceiptLogo>();

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = document.createElement('img');
    // Cloudinary allows cross-origin reads; without this the canvas is tainted.
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('could not load the logo'));
    img.src = url;
  });
}

/** Draw the fitted logo on white and read it back as one gray byte per pixel. */
function toGray(
  img: HTMLImageElement,
  widthDots: number,
): { gray: Uint8Array; width: number; height: number } {
  const { width, height } = fitLogo(
    img.naturalWidth,
    img.naturalHeight,
    widthDots,
  );
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas unavailable');
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);
  const { data } = ctx.getImageData(0, 0, width, height);
  const gray = new Uint8Array(width * height);
  for (let i = 0; i < gray.length; i++) {
    gray[i] = Math.round(
      data[i * 4] * 0.299 + data[i * 4 + 1] * 0.587 + data[i * 4 + 2] * 0.114,
    );
  }
  return { gray, width, height };
}

/**
 * The logo for this paper width, or null when it can't be had; never throws,
 * so a missing logo never stops a receipt.
 */
export async function loadReceiptLogo(
  url: string,
  widthDots: number,
): Promise<ReceiptLogo | null> {
  const key = `${widthDots}|${url}`;
  const cached = memory.get(key);
  if (cached) return cached;
  try {
    const { gray, width, height } = toGray(await loadImage(url), widthDots);
    const logo = rasterizeLogo(gray, width, height, widthDots);
    memory.set(key, logo);
    return logo;
  } catch (err) {
    console.log(
      LOG,
      'printing without logo:',
      err instanceof Error ? err.message : String(err),
    );
    return null;
  }
}
